import {computed, nextTick, onBeforeUnmount, onMounted, shallowRef, toValue, watch, type MaybeRefOrGetter} from 'vue';
import {NavbarMode} from '@navbar-unlogged-types';
import {useLocalePrefix, useRoute} from '#imports';
import {useDisplayMobile} from '@malt/utils';
import {useNavbarTransparentMode} from './useHostScopedFeatureFlag';
import {useNavbarMode} from './useNavbarMode';

/** Client home, freelancer home, the public `/freelancers` alias, and the Prismic preview of those homes. */
const TRANSPARENT_NAVBAR_PATHS = new Set(['/', '/client', '/freelancer', '/freelancers', '/cms/prismic/preview', '/c/homepage-client']);

/**
 * Overlays the bar on the client and freelancer homepages until the user scrolls,
 * when transparent mode is enabled for the current host.
 * On desktop the bar stays transparent until NavbarTop (`--navbar-top-height`) has scrolled away.
 * An open mobile menu restores the solid bar, because the drawer is an opaque panel.
 * The resources center also lives at `/` and keeps the solid bar.
 */
export function useNavbarTransparency(isMenuOpen: MaybeRefOrGetter<boolean>) {
    const route = useRoute();
    const {withoutLocalePrefix} = useLocalePrefix();
    const {hasMultipleModes, selectedMode} = useNavbarMode();
    const {isDisplayMobile} = useDisplayMobile('Sm');
    const isTransparentModeEnabled = useNavbarTransparentMode();
    const isScrolled = shallowRef(false);

    const isOverlay = computed(() => {
        const barePath = bareNavbarPath(withoutLocalePrefix(route.path));
        return isTransparentModeEnabled.value && TRANSPARENT_NAVBAR_PATHS.has(barePath) && selectedMode.value !== NavbarMode.RESOURCES;
    });

    const isTransparent = computed(() => isOverlay.value && !isScrolled.value && !toValue(isMenuOpen));

    function readScroll() {
        if (!import.meta.client) {
            return;
        }
        // NavbarTop is in normal flow only on desktop, and only when several modes are shown.
        const navbarTopHeight = !isDisplayMobile.value && hasMultipleModes.value ? readNavbarTopHeight() : 0;
        isScrolled.value = window.scrollY > 0 && window.scrollY >= navbarTopHeight;
    }

    onMounted(() => {
        readScroll();
        window.addEventListener('scroll', readScroll, {passive: true});
        window.addEventListener('resize', readScroll, {passive: true});
    });

    onBeforeUnmount(() => {
        window.removeEventListener('scroll', readScroll);
        window.removeEventListener('resize', readScroll);
    });

    watch(isDisplayMobile, readScroll);

    watch(
        () => route.path,
        () => {
            if (!import.meta.client) {
                return;
            }
            nextTick(readScroll);
        },
    );

    return {isOverlay, isTransparent};
}

function readNavbarTopHeight(): number {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--navbar-top-height');
    const height = Number.parseFloat(raw);
    return Number.isFinite(height) ? height : 0;
}

function bareNavbarPath(path: string): string {
    if (path.length > 1 && path.endsWith('/')) {
        return path.slice(0, -1);
    }
    return path;
}
